import React, { useState } from "react";
import Table from "../../../../components/elements/table/Table";
import Image from "../../../../components/elements/Image";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest } from "../../../../util/apiUtils";
import { useSelector } from "react-redux";
import {
  accountCheck,
  stripeReconnect,
} from "../../../../services/eventService";
import { toast } from "react-toastify";
import { classNames } from "../../../../helpers/classNames";
import DeleteModal from "../../../../components/common/DeleteModal";
import { useTranslation } from "react-i18next";

function generateAccountErrorArray(data) {
  if (!data || !data.currently_due || data.currently_due.length === 0) {
    return {};
  }

  // Categorize fields
  const businessFields = [
    "business_profile.mcc",
    "business_profile.product_description",
    "business_profile.support_phone",
    "business_profile.url",
    "external_account",
  ];

  const individualFields = [
    "individual.address.city",
    "individual.address.line1",
    "individual.address.postal_code",
    "individual.address.state",
    "individual.dob.day",
    "individual.dob.month",
    "individual.dob.year",
    "individual.last_name",
    "individual.phone",
    "individual.ssn_last_4",
  ];

  const tosFields = ["tos_acceptance.date", "tos_acceptance.ip"];

  // Helper function to filter fields dynamically
  const filterFields = (fields) =>
    fields.filter((f) => data.currently_due.includes(f));

  const businessMissing = filterFields(businessFields);
  const individualMissing = filterFields(individualFields);
  const tosMissing = filterFields(tosFields);

  const errors = {
    business: { message: "Business Info Missing", errors: [] },
    individual: { message: "Individual Info Missing", errors: [] },
    TOS: { message: "TOS Missing", errors: [] },
  };

  // Push into respective arrays
  if (businessMissing.length) {
    businessMissing.forEach((f) => {
      const label = f.split(".").pop().replace(/_/g, " ");
      errors.business.errors.push(
        label.charAt(0).toUpperCase() + label.slice(1)
      );
    });
  }

  if (individualMissing.length) {
    individualMissing.forEach((f) => {
      const label = f.split(".").pop().replace(/_/g, " ");
      errors.individual.errors.push(
        label.charAt(0).toUpperCase() + label.slice(1)
      );
    });
  }

  if (tosMissing.length) {
    tosMissing.forEach((f) => {
      const label = f.split(".").pop().replace(/_/g, " ");
      errors.TOS.errors.push(label.charAt(0).toUpperCase() + label.slice(1));
    });
  }

  return errors;
}

function isStripeDisabled(data) {
  if (
    !data?.stripe &&
    data?.stripeData?.currently_due?.length === 0 &&
    data?.stripeData?.errors?.length === 0 &&
    data?.stripeData?.disabled_reason === "requirements.pending_verification"
  ) {
    return true;
  } else {
    return false;
  }
}

export default function PaymentSettings() {
  const { t } = useTranslation();
  const { data: selectedOrganizationData } = useSelector(
    (state) => state?.organization?.selectedOrganizationDetails
  );
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    data: null,
  });

  const { data: profileData, refetch: refetchProfile } = useQuery({
    queryKey: ["profile"],
    queryFn: async () => {
      const res = await apiRequest("get", "profile");
      return res?.data?.data;
    },
  });

  const {
    data: accountData,
    isLoading: isAccountLoading,
    refetch: refetchAccountData,
  } = useQuery({
    queryKey: ["account-status", selectedOrganizationData?.userData?._id], // or whatever unique key is for user
    queryFn: async () => {
      const res = await accountCheck({
        user: selectedOrganizationData?.userData?._id,
      });

      return res?.data?.data;
    },
    enabled: !!selectedOrganizationData?.userData?._id,
  });

  const { mutate: addPaymentDetailsHandler, isPending: isStripePending } =
    useMutation({
      mutationFn: async (val) => {
        const res = await apiRequest("post", "stripe/account/connect", {
          body: {},
        });
        return res?.data?.data;
      },
      onSuccess: (res) => {
        if (res?.status === 200) {
          refetchProfile();
          if (res?.data?.onboardingUrl) {
            window.open(
              res?.data?.onboardingUrl,
              "_blank",
              "noopener,noreferrer"
            );
          }
        }
      },
    });

  const { mutate: reconnectStripeAccount, isPending: isStripeReconnecting } =
    useMutation({
      mutationFn: stripeReconnect,
      onSuccess: (res) => {
        const url = res?.data?.data?.onboardingUrl;

        if (url) {
          window.open(url, "_blank", "noopener,noreferrer");
        } else {
          toast.error("Stripe onboarding URL missing.");
        }
      },
      onError: (err) => {
        toast.error(
          err?.response?.data?.message || "Failed to reconnect to Stripe"
        );
      },
    });

  const {
    mutate: addPaypalPaymentDetailsHandler,
    data: paypalData,
    isPending: isPaypalPending,
  } = useMutation({
    mutationFn: async (val) => {
      const res = await apiRequest("post", "paypal/account/connect", {
        body: {},
      });
      return res?.data?.data;
    },
    onSuccess: (res) => {
      if (res?.status === 200) {
        window.open(res?.data?.onboardingUrl, "_blank");
      }
    },
  });

  const paymentSystem = [
    {
      id: "stripe",
      buttonLabel: t(
        isStripePending || isStripeReconnecting || isAccountLoading
          ? "Please wait process in pending"
          : accountData?.stripe
          ? "Stripe connected"
          : !accountData?.stripe &&
            Object.keys(accountData?.stripeData)?.length === 0
          ? "Connect to Stripe"
          : !accountData?.stripe &&
            Object.keys(accountData?.stripeData)?.length > 0 &&
            "Reconnect to stripe"
      ),
      icon: "https://img.icons8.com/color/48/stripe.png",
      disabled:
        isAccountLoading ||
        isStripePending ||
        isStripeReconnecting ||
        isStripeDisabled(accountData) ||
        accountData?.stripe,
      onClick: (e) => {
        if (
          !accountData?.stripe &&
          Object.keys(accountData?.stripeData)?.length === 0
        ) {
          addPaymentDetailsHandler();
          return;
        }
        if (
          !accountData?.stripe &&
          Object.keys(accountData?.stripeData)?.length > 0
        ) {
          reconnectStripeAccount();
          return;
        }
      },
    },
    {
      id: "paypal",
      buttonLabel: isAccountLoading
        ? "Please wait process in pending"
        : accountData?.paypal
        ? "Paypal connected"
        : "Connect to Paypal",
      icon: "https://img.icons8.com/color/48/paypal.png",
      disabled: accountData?.paypal || isAccountLoading,
      onClick: (e) => {
        if (!accountData?.paypal) {
          addPaypalPaymentDetailsHandler();
        }
      },
    },
  ];

  const tableHeadData = [
    { _id: "email", name: t("Email"), align: "left", type: "text" },
    { _id: "type", name: t("Payment Type"), align: "left", type: "text" },
    {
      _id: "Connected",
      name: t("Status"),
      align: "left",
      type: "text",
    },
    ...(accountData &&
    !accountData?.stripe &&
    Object.keys(accountData?.stripeData)?.length > 0
      ? [
          {
            _id: "action",
            name: t("Action"),
            align: "right",
            type: "action",
          },
        ]
      : []),
  ];

  const tableData = profileData?.paymentAccounts
    .filter((item) => item?.providerType === "account")
    ?.map((item) => {
      return {
        _id: item?._id,
        rowData: [
          {
            type: "text",
            align: "left",
            data: item?.email ?? "",
          },
          {
            type: "text",
            align: "left",
            data: (
              <div className="text-sm text-gray-700">
                <span className="inline-flex items-center gap-1 bg-primary/10 text-primary text-xs font-medium px-2 py-1 mt-2 rounded capitalize">
                  <span
                    className={`inline-block w-4 h-3 bg-cover bg-center ${
                      item?.provider === "stripe"
                        ? 'bg-[url("https://img.icons8.com/color/48/stripe.png")]'
                        : 'bg-[url("https://img.icons8.com/color/48/paypal.png")]'
                    }`}
                  />
                  {item?.provider}
                </span>
              </div>
            ),
          },
          {
            type: "text",
            align: "left",
            data:
              item?.provider === "stripe"
                ? accountData?.stripe === true
                  ? "connected"
                  : accountData?.stripe === false
                  ? "not connected"
                  : "pending"
                : item?.provider === "paypal"
                ? accountData?.paypal === true
                  ? "connected"
                  : accountData?.paypal === false
                  ? "not connected"
                  : "pending"
                : "pending",
          },
          ...(item?.provider === "stripe" &&
          accountData &&
          !accountData?.stripe &&
          Object.keys(accountData?.stripeData)?.length > 0
            ? [
                {
                  _id: "action",
                  type: "action",
                  align: "right",
                  cellClass: "w-[5%]",
                  actionData: [
                    {
                      _id: "Delete",
                      name: t("Delete"),
                      icon: "fa-regular fa-trash-can",
                      onClick: () => {
                        setDeleteModal((pre) => ({ data: item, isOpen: true }));
                      },
                      standout: true,
                    },
                  ],
                },
              ]
            : []),
        ],
      };
    });

  const { mutate: deleteHandler, isPending: deletePending } = useMutation({
    mutationFn: async (payload) => {
      const res = await apiRequest("patch", "payment-gateway/account/delete", {
        body: { gatewayType: payload?.provider },
      });
      return res?.data?.data;
    },
    onSuccess: (res) => {
      if (res?.status === 200) {
        refetchAccountData();
        refetchProfile();
        setDeleteModal({ isOpen: false, data: null });
      } else {
        toast.warning(res?.message || res?.error);
      }
    },
    onError: (error) => {
      console.log(error);
    },
  });

  return (
    <>
      <div className="mx-auto p-4 bg-white shadow rounded-md">
        <div className="py-4 md:py-8 px-3 md:px-6 rounded-md space-y-4 border border-gray-200">
          <div className="space-y-4">
            <div className="relative space-y-4">
              <div className="text-xl md:text-2xl text-deep-purple font-bold font-Montserrat mb-2">
                {t("Payment management")}
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-3 md:px-6 divide-y divide-slate-200">
              <div className="py-6">
                <div>
                  <h2 className="text-xl font-semibold text-deep-purple mb-1">
                    {t("Online payments")}
                  </h2>
                  <div className="mb-6 text-sm text-text-secondary font-[300]">
                    {t("Simple & secure way for customers to pay.")}
                  </div>
                </div>
                <div className="flex flex-col md:flex-row items-center gap-3">
                  {paymentSystem?.map((payment, i) => (
                    <button
                      type="button"
                      key={i?.toString()}
                      className={classNames(
                        "bg-primary-gradient font-medium text-sm text-white rounded-full px-5 py-3 flex items-center gap-2 w-full md:w-auto",
                        payment?.disabled
                          ? "bg-gray-300 cursor-not-allowed opacity-40"
                          : ""
                      )}
                      onClick={payment?.onClick}
                      disabled={payment?.disabled}
                    >
                      <Image
                        src={payment?.icon}
                        alt={payment?.icon}
                        classes={"w-6 h-6 object-contain object-center"}
                      />
                      {t(payment?.buttonLabel)}
                    </button>
                  ))}
                </div>
                {!accountData?.stripe &&
                  (accountData?.stripeData?.errors?.length > 0 ||
                    accountData?.stripeData?.currently_due?.length > 0) && (
                    <div className="py-3 px-4 bg-red-500/10 rounded-lg mt-4">
                      <h3 className="text-base font-medium text-red-500">
                        {t("Stripe Error")} :<br />
                        <div className="text-xs mt-1 flex items-center">
                          <span className="flex mr-2">
                            <i className="fa-regular fa-circle-info text-base"></i>
                          </span>
                          {t("To connect your payment account please click on")}
                          <span className="inline-block underline font-bold mx-1">
                            {t("reconnect stripe")}
                          </span>{" "}
                          {t("button and provide your valid information")}
                        </div>
                      </h3>
                      {Object.keys(
                        generateAccountErrorArray(accountData?.stripeData)
                      )?.map(
                        (key, j) =>
                          generateAccountErrorArray(accountData?.stripeData)?.[
                            key
                          ]?.errors?.length > 0 && (
                            <div key={j?.toString()} className=" text-red-500">
                              <div className="text-xs mt-1 flex items-center">
                                <span className="flex mr-2">
                                  <i className="fa-regular fa-circle-info text-base"></i>
                                </span>
                                <div className="text-xs font-medium flex items-center">
                                  {
                                    generateAccountErrorArray(
                                      accountData?.stripeData
                                    )?.[key]?.message
                                  }
                                </div>
                              </div>
                              <ul className="ml-5">
                                {generateAccountErrorArray(
                                  accountData?.stripeData
                                )?.[key]?.errors?.map((err, i) => (
                                  <li
                                    key={i?.toString()}
                                    className="text-xs text-red-500 font-medium list-disc list-inside"
                                  >
                                    {err}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )
                      )}
                      <ul className="space-y-2">
                        {accountData?.stripeData?.errors?.map((err, i) => (
                          <li
                            key={i?.toString()}
                            className="text-xs text-red-500 font-medium list-disc list-inside"
                          >
                            {err?.reason}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                {isStripeDisabled(accountData) && (
                  <div className="text-blue-400 text-sm mt-4 font-medium flex items-center">
                    <span className="flex mr-2">
                      <i className="fa-regular fa-circle-info text-base"></i>
                    </span>
                    {t(
                      "Please wait while we verify the information you have provided. This process may take 1–2 business days to complete."
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Table */}
            <Table
              tableHeadData={tableHeadData}
              tableData={tableData}
              isLoder={false}
              pageCount={1}
              currentPage={1}
              list={[]}
              originalData={tableData}
              setOriginalData={() => {}}
              isOrderingDisabled={true}
              tableClasses="border border-gray-100"
            />
          </div>
          {/* <Modal
        isOpen={!!data?.data?.onboardingUrl}
        closeBtn={() => {
          reset();
        }}
        setIsOpen={() => {
          reset();
        }}
      >
        <embed src={data?.data?.onboardingUrl} />
      </Modal> */}
        </div>
      </div>
      <DeleteModal
        open={deleteModal?.isOpen}
        loading={deletePending}
        onClose={() => setDeleteModal((pre) => ({ data: null, isOpen: false }))}
        deleteHandler={() => {
          deleteHandler(deleteModal?.data);
        }}
      />
    </>
  );
}
